/**
 * Firebase Configuration Service
 * Provides consistent Firebase configuration across the app and service worker
 */

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

/**
 * Get Firebase configuration from environment variables
 */
export const getFirebaseConfig = (): FirebaseConfig => {
  const config: FirebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  };

  // Validate required fields
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missingFields = requiredFields.filter(field => !config[field as keyof FirebaseConfig]);
  
  if (missingFields.length > 0) {
    throw new Error(`Missing Firebase configuration: ${missingFields.join(', ')}`);
  }

  return config;
};

/**
 * Get VAPID key for push notifications
 */
export const getVapidKey = (): string => {

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  
  if (!vapidKey) {
    throw new Error('VITE_FIREBASE_VAPID_KEY is not configured');
  }
  return vapidKey;
};

/**
 * Check if Firebase is configured
 */
export const isFirebaseConfigured = (): boolean => {
  try {
    getFirebaseConfig();
    return true;
  } catch {
    return false;
  }
};

/**
 * Get Firebase configuration as JSON string (for service worker)
 */
export const getFirebaseConfigJSON = (): string => {
  return JSON.stringify(getFirebaseConfig());
};